import React, { useEffect, useMemo, useRef, useState } from 'react';
import QRCode from 'qrcode';
import {
  CheckCircle2,
  Download,
  Image as ImageIcon,
  Loader2,
  RefreshCw,
  Sparkles,
  Upload,
  XCircle,
  Eye,
} from 'lucide-react';
import { chatApi } from '../lib/api';
import XuanYuanReportFooter from './XuanYuanReportFooter';
import XuanYuanTopBar from './XuanYuanTopBar';

interface BeautyFaceTestProps {
  onBack: () => void;
  onRequireLogin?: () => void;
  onBeforeShowResult?: (testId: string) => Promise<boolean>;
}

type BeautyGender = 'male' | 'female';

interface BeautySectionScore {
  score: number;
  feature: string;
  highlight: string;
  optimize: string;
  complexionFit: string;
  style: string;
}

interface BeautyReport {
  overallScore: number;
  overallEightWords: string;
  keywords: string[];
  overallSummary: string;
  structuredFeatures: {
    faceShape: string;
    eyeType: string;
    browType: string;
    noseType: string;
    lipType: string;
    skinCondition: string;
    symmetryObservation: string;
    proportion: {
      threeCourtTop: number;
      threeCourtMiddle: number;
      threeCourtBottom: number;
      fiveEyeRatio: number;
      interocularRatio: number;
      goldenRatioFit: number;
    };
  };
  styleClassification: {
    primary: string;
    secondary: string;
    confidence: number;
    evidence: string;
    styleTags: string[];
  };
  facialAnalysis: {
    eyes: BeautySectionScore;
    nose: BeautySectionScore;
    lips: BeautySectionScore;
    faceShape: BeautySectionScore;
  };
  boneAnalysis: {
    contourDepth: number;
    cheekboneShape: number;
    jawline: number;
    foreheadFullness: number;
    summary: string;
  };
  skinAnalysis: {
    texture: number;
    toneEvenness: number;
    fineness: number;
    radiance: number;
    skinType: string;
    summary: string;
  };
  beautyType: {
    typeName: string;
    detailText: string;
    coreAdvantages: string;
    styleAdvice: string;
  };
  makeupAdvice: {
    base: string;
    eyes: string;
    noseAndLips: string;
    contourAndStyle: string;
  };
  improvementPlan: {
    diagnosis: string;
    focusedCare: string;
    makeupOptimization: string;
    hairAndOutfit: string;
    longTermMaintenance: string;
  };
}

interface BeautyStructuredExtraction {
  structuredFeatures: BeautyReport['structuredFeatures'];
  styleClassification: BeautyReport['styleClassification'];
}

interface BeautyAnalysisMetrics {
  totalMs: number;
  visionExtractMs: number;
  reportGenerateMs: number;
  visionModel: string;
  reportModel: string;
}

interface BeautyAnalyzeResult {
  report: BeautyReport;
  metrics: BeautyAnalysisMetrics;
}

type BeautyHistoryRecord = {
  id: string;
  createdAt: number;
  gender: BeautyGender;
  report: BeautyReport;
};

const EXAMPLE_REPORT_IMAGE_URL =
  'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=900&h=1200&fit=crop&crop=face';

interface GenderTheme {
  pageBgClass: string;
  panelGradientClass: string;
  primaryButtonClass: string;
  textAccentClass: string;
  softBgClass: string;
  borderClass: string;
  chipClass: string;
  scoreTextClass: string;
  ringTrack: string;
  ringStroke: string;
  chartStroke: string;
  chartFill: string;
}

const ANALYSIS_STEPS = ['识别人像结构', '解析五官细节', '评估骨皮状态', '生成风格建议', '整理最终报告'];
const DEFAULT_ESTIMATED_ANALYSIS_SECONDS = { min: 48, max: 98 } as const;
const PROGRESS_EXPECTED_MS = 70000;
const SITE_URL = 'https://qimen.aifashou.cn';
const BEAUTY_TIMING_HISTORY_KEY = 'beauty_face_timing_history_v1';
const MAX_TIMING_HISTORY = 12;

const BEAUTY_LOGIC_KB = [
  '【黄金比例参考】',
  '1) 三庭比例接近 1:1:1 更协调（允许轻微偏差）。',
  '2) 五眼比例理想值约为 5（全脸宽度≈5个眼宽）。',
  '3) 眼间距与单眼宽接近 1:1 更自然。',
  '4) 左右对称度与中轴稳定度越高，镜头耐看度越高。',
  '【风格分类参考】',
  '1) 浓颜：五官对比强、量感重、轮廓清晰。',
  '2) 淡颜：五官柔和、对比低、氛围清透。',
  '3) 幼态脸：眼位偏开、面部线条圆润、亲和感强。',
  '4) 冷感脸：线条利落、骨相感更强、清冷高级。',
  '5) 甜酷：柔和与锐利并存，对比感明显。',
  '6) 知性气质：比例稳定、五官均衡、整体克制。',
].join('\n');
const BEAUTY_HISTORY_KEY = 'beauty_face_history_v1';
const MAX_BEAUTY_HISTORY = 8;

const BEAUTY_STRUCTURED_PROMPT = `你是“人像视觉结构化提取引擎”，只做客观特征提取，不做主观评价，不给颜值分，不输出建议。

硬性约束：
1. 仅输出 JSON，不要 Markdown，不要解释。
2. 不得输出“无法识别/看不清/无法分析”等中断语句。
3. 输出尽量简洁，字段要完整，文本避免空洞套话。
4. 比例字段尽量给出合理数值，无法精确时给最可能估计值。

请输出以下 JSON：
{
  "structuredFeatures": {
    "faceShape": "脸型结构描述",
    "eyeType": "眼型结构描述",
    "browType": "眉骨/眉形结构描述",
    "noseType": "鼻型结构描述",
    "lipType": "唇型结构描述",
    "skinCondition": "肤质与皮肤状态",
    "symmetryObservation": "中轴/对称度观察结论",
    "proportion": {
      "threeCourtTop": 0-5,
      "threeCourtMiddle": 0-5,
      "threeCourtBottom": 0-5,
      "fiveEyeRatio": 0-8,
      "interocularRatio": 0-3,
      "goldenRatioFit": 0-10
    }
  },
  "styleClassification": {
    "primary": "浓颜/淡颜/幼态脸/冷感脸/甜酷/知性气质 之一",
    "secondary": "次风格",
    "confidence": 0-100整数,
    "evidence": "60字以上，说明判定依据",
    "styleTags": ["标签1","标签2","标签3"]
  }
}`;

const THEMES: Record<BeautyGender, GenderTheme> = {
  male: {
    pageBgClass: 'bg-[linear-gradient(180deg,#f2f8ff_0%,#eef8ff_45%,#f9fbff_100%)]',
    panelGradientClass: 'from-sky-500 to-blue-600',
    primaryButtonClass: 'bg-blue-600 hover:bg-blue-700',
    textAccentClass: 'text-blue-700',
    softBgClass: 'bg-sky-50',
    borderClass: 'border-sky-100',
    chipClass: 'bg-sky-100 text-sky-700',
    scoreTextClass: 'text-blue-600',
    ringTrack: '#dbeafe',
    ringStroke: '#0284c7',
    chartStroke: '#0284c7',
    chartFill: 'rgba(2,132,199,0.12)',
  },
  female: {
    pageBgClass: 'bg-[linear-gradient(180deg,#fff6fa_0%,#fff4f9_45%,#fffafc_100%)]',
    panelGradientClass: 'from-rose-500 to-pink-600',
    primaryButtonClass: 'bg-rose-600 hover:bg-rose-700',
    textAccentClass: 'text-rose-700',
    softBgClass: 'bg-rose-50',
    borderClass: 'border-rose-100',
    chipClass: 'bg-rose-100 text-rose-700',
    scoreTextClass: 'text-rose-600',
    ringTrack: '#ffe4ea',
    ringStroke: '#e11d48',
    chartStroke: '#e11d48',
    chartFill: 'rgba(225,29,72,0.12)',
  },
};

const BEAUTY_REPORT_PROMPT = `你是一位资深人像美学顾问，请根据用户上传照片，输出“非常详细、专业、积极且可执行”的颜值分析报告。

硬性约束：
1. 报告必须比常见模板更详细，禁止一句话敷衍。
2. 语气积极，不得贬低用户；优化建议使用“锦上添花”话术。
3. 禁止输出“无法识别/看不清/无法分析”等中断语句。
4. 仅输出 JSON，不要 Markdown，不要解释。
5. overallEightWords 必须是 8 个中文字符。
6. keywords 必须给 3 个短词。
7. 五官、骨相、皮相分项都必须有评分；五官各项须包含“特征、优势亮点、优化方向、适配风格”。
8. 采用严格评分标准：7.0 分代表中等水平，8.5 分以上必须非常少见，不要默认给高分。
9. 所有分数统一为 10 分制（可保留 1 位小数）。
10. 必须先做“结构化特征提取”，再给分和建议，所有结论都要能回溯到结构化特征。
11. 必须结合黄金比例与风格分类，不可只给空泛夸赞。
12. 五官四项都必须写到这 5 个维度：特征、优势亮点、优化方向、肤色适配、适配风格。
13. 骨相总结与皮相总结要覆盖这 5 个维度的信息：特征、优势亮点、优化方向、肤色适配、适配风格。
14. 化妆建议必须个性化引用结构化字段（如肤质、眼型、鼻型、唇型），不能写通用模板句。

以下是分析知识库，请在输出时显式用到：
${BEAUTY_LOGIC_KB}

请严格按以下 JSON 输出：
{
  "overallScore": 0-10（可含1位小数）,
  "overallEightWords": "8字总体评价",
  "keywords": ["关键词1","关键词2","关键词3"],
  "overallSummary": "120字以上总体表现",
  "structuredFeatures": {
    "faceShape": "脸型结构描述",
    "eyeType": "眼型结构描述",
    "browType": "眉骨/眉形结构描述",
    "noseType": "鼻型结构描述",
    "lipType": "唇型结构描述",
    "skinCondition": "肤质与皮肤状态",
    "symmetryObservation": "中轴/对称度观察结论",
    "proportion": {
      "threeCourtTop": 0-5,
      "threeCourtMiddle": 0-5,
      "threeCourtBottom": 0-5,
      "fiveEyeRatio": 0-8,
      "interocularRatio": 0-3,
      "goldenRatioFit": 0-10
    }
  },
  "styleClassification": {
    "primary": "浓颜/淡颜/幼态脸/冷感脸/甜酷/知性气质 之一",
    "secondary": "次风格",
    "confidence": 0-100整数,
    "evidence": "80字以上，说明风格判定依据",
    "styleTags": ["标签1","标签2","标签3"]
  },
  "facialAnalysis": {
    "eyes": { "score": 0-10, "feature": "40字以上", "highlight": "40字以上", "optimize": "40字以上", "complexionFit": "40字以上", "style": "30字以上" },
    "nose": { "score": 0-10, "feature": "40字以上", "highlight": "40字以上", "optimize": "40字以上", "complexionFit": "40字以上", "style": "30字以上" },
    "lips": { "score": 0-10, "feature": "40字以上", "highlight": "40字以上", "optimize": "40字以上", "complexionFit": "40字以上", "style": "30字以上" },
    "faceShape": { "score": 0-10, "feature": "40字以上", "highlight": "40字以上", "optimize": "40字以上", "complexionFit": "40字以上", "style": "30字以上" }
  },
  "boneAnalysis": {
    "contourDepth": 0-10,
    "cheekboneShape": 0-10,
    "jawline": 0-10,
    "foreheadFullness": 0-10,
    "summary": "120字以上"
  },
  "skinAnalysis": {
    "texture": 0-10,
    "toneEvenness": 0-10,
    "fineness": 0-10,
    "radiance": 0-10,
    "skinType": "油皮/干皮/混合肌/中性肌 之一",
    "summary": "120字以上"
  },
  "beautyType": {
    "typeName": "如 潜力养成型/镜头高级型",
    "detailText": "160字以上，使用“根据您上传的照片精准分析...”风格",
    "coreAdvantages": "120字以上，描述照片核心专属优势",
    "styleAdvice": "120字以上，描述照片风格专属建议"
  },
  "makeupAdvice": {
    "base": "120字以上，底妆建议",
    "eyes": "120字以上，眼妆建议",
    "noseAndLips": "120字以上，鼻型与唇妆建议",
    "contourAndStyle": "120字以上，修容与整体风格建议"
  },
  "improvementPlan": {
    "diagnosis": "140字以上，含最终打分总结",
    "focusedCare": "140字以上，按肤质给护理方案",
    "makeupOptimization": "140字以上，底妆眼妆唇妆修容优化",
    "hairAndOutfit": "140字以上，发型穿搭建议",
    "longTermMaintenance": "140字以上，长期维护建议"
  }
}`;

const clamp = (n: number, min: number, max: number): number => Math.max(min, Math.min(max, n));

const formatHistoryTime = (ts: number): string => {
  if (!Number.isFinite(ts) || ts <= 0) return '--';
  return new Date(ts).toLocaleString('zh-CN', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const toEstimatedRange = (msList: number[]): { min: number; max: number } => {
  const seconds = msList
    .map((item) => Number(item) / 1000)
    .filter((item) => Number.isFinite(item) && item > 0)
    .sort((a, b) => a - b);
  if (seconds.length < 2) return { ...DEFAULT_ESTIMATED_ANALYSIS_SECONDS };
  const low = seconds[Math.max(0, Math.floor((seconds.length - 1) * 0.2))];
  const high = seconds[Math.max(0, Math.floor((seconds.length - 1) * 0.85))];
  const min = Math.max(35, Math.floor(low * 0.95));
  const max = Math.min(130, Math.ceil(high * 1.15));
  return {
    min,
    max: Math.max(min + 8, max),
  };
};

const loadBeautyTimingHistory = (): number[] => {
  try {
    const raw = localStorage.getItem(BEAUTY_TIMING_HISTORY_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((item) => Number(item))
      .filter((item) => Number.isFinite(item) && item > 0)
      .slice(-MAX_TIMING_HISTORY);
  } catch {
    return [];
  }
};

const appendBeautyTimingHistory = (durationMs: number): number[] => {
  const safeDuration = Math.max(1000, Math.round(Number(durationMs) || 0));
  const next = [...loadBeautyTimingHistory(), safeDuration].slice(-MAX_TIMING_HISTORY);
  try {
    localStorage.setItem(BEAUTY_TIMING_HISTORY_KEY, JSON.stringify(next));
  } catch {
    // noop
  }
  return next;
};

const extractJsonText = (raw: string): string => {
  const text = String(raw || '').replace(/^\uFEFF/, '').trim();
  if (!text) return text;
  const block = text.match(/```(?:json)?\s*([\s\S]*?)```/i);
  if (block?.[1]) return block[1].trim();
  const start = text.indexOf('{');
  if (start < 0) return text;
  let depth = 0;
  let inStr = false;
  let escaped = false;
  for (let i = start; i < text.length; i += 1) {
    const ch = text[i];
    if (inStr) {
      if (escaped) {
        escaped = false;
        continue;
      }
      if (ch === '\\') {
        escaped = true;
        continue;
      }
      if (ch === '"') inStr = false;
      continue;
    }
    if (ch === '"') {
      inStr = true;
      continue;
    }
    if (ch === '{') depth += 1;
    if (ch === '}') {
      depth -= 1;
      if (depth === 0) return text.slice(start, i + 1).trim();
    }
  }
  return text.slice(start).trim();
};

const toText = (value: unknown, fallback = ''): string => {
  const txt = String(value ?? '').trim();
  return txt || fallback;
};

const ensureLength = (text: string, minLength: number, fallback: string): string => {
  const finalText = String(text || '').trim();
  return finalText.length >= minLength ? finalText : fallback;
};

const toList = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];
  return value.map((item) => String(item ?? '').trim()).filter(Boolean);
};

const toBoundedNumber = (value: unknown, min: number, max: number, fallback: number): number => {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return clamp(n, min, max);
};

const strictCalibrateScore10 = (raw: number): number => {
  let n = clamp(raw, 0, 10);
  // 压缩高分区间，避免大部分样本集中在 8.5+。
  if (n >= 8.5) n = 8.5 + (n - 8.5) * 0.45;
  // 将整体分布向中间收敛，保留少量高分。
  n = 7.0 + (n - 7.0) * 0.9;
  return clamp(Math.round(n * 10) / 10, 4.8, 9.3);
};

const toScore10 = (value: unknown, fallback = 7.2): number => {
  const n = Number(value);
  if (!Number.isFinite(n)) return strictCalibrateScore10(fallback);
  const normalized = n > 10 && n <= 100 ? n / 10 : n;
  return strictCalibrateScore10(normalized);
};

const average = (values: number[]): number => (
  values.length ? values.reduce((sum, num) => sum + num, 0) / values.length : 0
);

const toConfidence100 = (value: unknown, fallback = 72): number => {
  const n = Number(value);
  if (!Number.isFinite(n)) return clamp(Math.round(fallback), 35, 98);
  return clamp(Math.round(n), 35, 98);
};

const normalizeEightWords = (value: unknown, fallback: string): string => {
  const raw = String(value ?? '').replace(/[，。,.、\s]/g, '').trim();
  const chars = Array.from(raw).filter((ch) => ch.trim());
  if (chars.length === 8) return chars.join('');
  if (chars.length > 8) return chars.slice(0, 8).join('');
  return fallback;
};

const parseLooseJson = (raw: string): unknown => JSON.parse(extractJsonText(raw || ''));

const loadImageDataUrl = (file: File): Promise<string> => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onerror = () => reject(new Error('图片读取失败'));
  reader.onload = () => resolve(String(reader.result || ''));
  reader.readAsDataURL(file);
});

const compressImage = async (dataUrl: string, maxSize = 1200, quality = 0.82): Promise<string> => {
  const img = new Image();
  const loaded = new Promise<void>((resolve, reject) => {
    img.onload = () => resolve();
    img.onerror = () => reject(new Error('图片解析失败'));
  });
  img.src = dataUrl;
  await loaded;
  const scale = Math.min(1, maxSize / Math.max(img.width, img.height));
  const width = Math.max(1, Math.round(img.width * scale));
  const height = Math.max(1, Math.round(img.height * scale));
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return dataUrl;
  ctx.drawImage(img, 0, 0, width, height);
  return canvas.toDataURL('image/jpeg', quality);
};

const buildDefaultSectionScore = (
  feature: string,
  highlight: string,
  optimize: string,
  complexionFit: string,
  style: string,
  score = 7.6,
): BeautySectionScore => ({ score, feature, highlight, optimize, complexionFit, style });

const defaultReportFor = (gender: BeautyGender): BeautyReport => {
  const isMale = gender === 'male';
  return {
    overallScore: isMale ? 7.6 : 7.8,
    overallEightWords: isMale ? '颜值中上五官端正' : '颜值中上清秀耐看',
    keywords: isMale ? ['骨相皮相双优', '镜头耐看', '清爽型男'] : ['骨相皮相双优', '气质高级', '耐看氛围'],
    overallSummary: isMale
      ? '你的整体颜值属于中上偏高区间，五官与轮廓的匹配度好，视觉观感干净利落。第一眼会给人“有精神、很顺眼”的印象，第二眼则能看到骨相与皮相的协同优势，具备明显的镜头稳定性。通过细节微调，你可以在成熟稳重与清爽少年感之间自由切换。'
      : '你的整体颜值属于中上偏高区间，五官比例协调且气质表达自然，属于典型的耐看型美感。你在镜头中有较高的面部完整度，动态和静态都能保持清爽与精致并存。通过少量风格强化，你能够进一步放大辨识度，形成更鲜明的个人形象标签。',
    structuredFeatures: {
      faceShape: isMale ? '偏长椭圆脸，面中与下颌衔接自然。' : '柔和椭圆偏心形脸，轮廓线条顺滑。',
      eyeType: isMale ? '平扇型双眼皮，眼裂长度中上。' : '开扇型双眼皮，眼尾微上扬。',
      browType: isMale ? '眉骨支撑适中，眉形平直偏利落。' : '眉骨柔和，眉弓过渡自然。',
      noseType: isMale ? '山根到鼻尖线条较顺，中轴稳定。' : '鼻梁细直，鼻尖圆润，立体度平衡。',
      lipType: isMale ? '上唇线条清晰，下唇体积适中。' : '唇峰明显，唇珠饱满，口角友好。',
      skinCondition: '混合肌倾向，T区易出油、两颊稳定度较好。',
      symmetryObservation: '面部中轴稳定，左右特征点差异较小，镜头稳定性较好。',
      proportion: {
        threeCourtTop: 1.0,
        threeCourtMiddle: 1.02,
        threeCourtBottom: 0.98,
        fiveEyeRatio: 4.9,
        interocularRatio: 1.02,
        goldenRatioFit: isMale ? 7.7 : 7.9,
      },
    },
    styleClassification: {
      primary: isMale ? '知性气质' : '淡颜',
      secondary: isMale ? '冷感脸' : '甜酷',
      confidence: 78,
      evidence: isMale
        ? '五官量感中等、对比度适中，骨相线条干净，整体呈现克制与利落并存的知性冷感。'
        : '五官柔和度较高、皮相通透感较好，同时眼唇有一定对比度，形成清透与个性并存的淡颜甜酷气质。',
      styleTags: isMale ? ['线条利落', '镜头稳定', '清爽耐看'] : ['清透氛围', '柔和立体', '耐看辨识'],
    },
    facialAnalysis: {
      eyes: buildDefaultSectionScore(
        '眼部轮廓清晰，眼神聚焦力较好，睁眼状态有明显精神感，属于容易建立第一好感的眼型结构。',
        '眼部动态表达自然，微笑时神态松弛，既不显疲态也不显僵硬，在照片和视频里都能稳定输出亲和与自信。',
        '可通过细化睫毛根部层次与卧蚕提亮来增强眼神立体度，避免大面积浓重色块压低眼神通透感。',
        isMale ? '建议偏中性冷调底妆，眼下提亮选择米灰系，能强化眼神清爽度。' : '建议中性偏暖底妆，卧蚕提亮用香槟粉调，更能放大灵动感。',
        isMale ? '适配清爽商务风、轻熟通勤风、简约都市风。' : '适配韩系清透风、法式温柔风、港风轻复古。',
        isMale ? 7.8 : 7.9,
      ),
      nose: buildDefaultSectionScore(
        '鼻梁到鼻尖过渡流畅，中轴支撑稳定，侧面线条相对干净，能够为面部结构提供良好的立体基底。',
        '鼻部比例与眼唇位置关系协调，正面观感不会突兀，侧面轮廓有自然层次，是提升高级感的关键加分项。',
        '修饰时建议强调鼻梁中段和鼻尖高光的纵向连接，阴影范围控制在窄区，避免过宽导致面中显重。',
        '鼻侧影建议使用灰棕或冷棕色系，避免偏红修容让鼻部显脏；高光选细闪低饱和更自然。',
        isMale ? '适配硬朗清爽风、学院休闲风、简洁质感风。' : '适配轻甜精致风、高级通勤风、氛围感妆容。',
        isMale ? 7.6 : 7.7,
      ),
      lips: buildDefaultSectionScore(
        '唇形边界较清楚，唇峰与嘴角关系自然，静态状态有柔和感，动态表达时不容易出现僵硬口部线条。',
        '唇部能明显提升面部亲和度，笑容呈现友好且有感染力，是增强“精致但不刻意”观感的重要位置。',
        '可通过精准唇线和分区上色提升立体度，上唇饱满度略补、下唇光泽度提升，整体会更显年轻有活力。',
        '唇色建议按肤色选择：偏黄肌更适合豆沙奶咖，偏白肌可用玫瑰浆果色提升通透感。',
        isMale ? '适配自然哑光风、低饱和健康风。' : '适配豆沙玫瑰风、水光唇釉风、轻雾面高级风。',
        isMale ? 7.4 : 7.6,
      ),
      faceShape: buildDefaultSectionScore(
        '脸型整体过渡顺畅，面部留白与结构线条有平衡，正侧脸转换时轮廓完整度较高，观感稳定。',
        '下颌线与面中衔接自然，骨相支撑力在线，既有亲和度又有一定立体感，属于可塑性较高的脸型基础。',
        '优化重点可放在发际线与颧侧明暗过渡，利用发型层次与修容路径增强纵向比例，提升镜头精致度。',
        '轮廓修容宜选低饱和灰棕，腮红以柔粉或杏色贴骨点位上提，能保持脸型干净不脏妆。',
        isMale ? '适配短发层次风、极简干净风、半正式轻商务风。' : '适配锁骨发气质风、法式慵懒风、港风卷发系。',
        isMale ? 7.9 : 8.0,
      ),
    },
    boneAnalysis: {
      contourDepth: isMale ? 7.8 : 7.6,
      cheekboneShape: isMale ? 7.3 : 7.4,
      jawline: isMale ? 7.9 : 7.7,
      foreheadFullness: isMale ? 7.2 : 7.5,
      summary: isMale
        ? '你的骨相基础属于“稳定支撑型”，轮廓立体度和下颌线清晰度都具备不错优势。颧骨形态不过分外扩，能保持成熟感同时不显凶。额头饱满度在日常自然状态下已足够支撑整体比例，若配合合适发型和光影，抗老观感与高级度都会进一步增强。'
        : '你的骨相基础属于“柔和支撑型”，轮廓线条顺而不塌，颧骨形态对面部立体感有正向贡献。下颌线在不同角度下都能维持较好完整度，额头饱满度也有利于打造轻盈和高级并存的氛围。整体骨相抗老潜力良好，后续通过造型会更容易放大优势。',
    },
    skinAnalysis: {
      texture: 7.6,
      toneEvenness: 7.5,
      fineness: 7.4,
      radiance: 7.6,
      skinType: '混合肌',
      summary: '你的皮相表现总体在线，肤质细腻度和光泽度在同龄人中有明显优势。肤色均匀度较好，说明基础屏障状态具备一定稳定性。若进一步优化 T 区油脂管理和两颊保湿平衡，皮肤通透感会更稳定，底妆服帖度、近景镜头质感和整体精致度都能明显提升。',
    },
    beautyType: {
      typeName: isMale ? '潜力提升型男生' : '潜力养成型女生',
      detailText: isMale
        ? '根据您上传的照片精准分析，您属于潜力提升型男生，提升空间非常可观。您的脸型基础稳定，面部比例和中轴关系协调，眼部有明确精神感，鼻部轮廓具备立体潜力，唇部线条自然亲和。稍加修饰即可放大骨相优势与干净感，在镜头下形成“清爽、可靠、耐看”的高完成度形象。'
        : '根据您上传的照片精准分析，您属于潜力养成型女生，提升空间极大。您的脸型基础优秀，眼部神态具有灵动与温柔并存的美感，鼻部中轴稳定且有精致化潜力，唇形条件对氛围感非常友好。稍加修饰即可让五官立体度和气质辨识度同步提升，形成高级耐看的个人风格。稍加打造就能实现颜值大幅提升。',
      coreAdvantages: isMale
        ? '照片核心加分项在于眼神清晰度、鼻部中轴稳定性以及脸型轮廓完整度。骨相支撑较稳，对中长期抗老表现有帮助；肤质在基础护理后有明显上升空间，精致度可持续提升。'
        : '照片核心加分项在于眼部神态、鼻部线条和脸型结构协同。你的骨相支撑力足够，具备抗老优势；肤质通过针对性护理后可进一步提升通透感，让精致度和镜头表现同步上扬。',
      styleAdvice: isMale
        ? '最适合你的风格是清爽都市风与轻熟质感风。重点突出眼鼻中轴与下颌线优势，服装建议优先纯色、低饱和和利落剪裁，能更好贴合你的轮廓特征，让整体观感从“顺眼”升级为“有质感的高级耐看”。'
        : '最适合你的风格是轻港风、高级通勤风与清透氛围风。重点突出眼神与唇部优势，同时保持底妆干净和轮廓柔和，能完全贴合你的面部特征，让整体风格更有记忆点且不失高级感。',
    },
    makeupAdvice: {
      base: '底妆建议以“分区管理”为核心：如果是混合肌，T 区优先控油、两颊重点保湿，底妆前先做轻薄妆前打底。粉底选择中等遮瑕、轻薄贴肤类型，局部叠加瑕疵修正，不建议全脸厚涂。定妆采用“中心定、边缘薄”的方式，既保证持妆又保留皮肤自然光泽。',
      eyes: '眼妆建议根据眼型做结构强化：先用浅中性色铺底，再用中深色在眼尾和睫毛根部增强纵深。眼线重点放在外眼角延伸，保持线条干净；卧蚕提亮应控制面积，避免过度肿胀感。睫毛走自然卷翘路线，强调根部密度，会显得精神且高级。',
      noseAndLips: '鼻型修饰建议采用窄阴影+中轴高光的组合，鼻梁中段轻扫阴影，鼻尖和山根高光提亮，增强立体但不突兀。唇妆可根据风格选择：日常用低饱和豆沙或奶咖色，氛围场景可用玫瑰调；上唇边缘轻勾、下唇中部提亮，能放大唇形优势。',
      contourAndStyle: '修容建议以“少量多次”原则，重点在颧侧、下颌边缘和发际线阴影过渡，避免过重导致脏感。腮红建议从颧侧向上轻扫，提升面部提拉感。整体风格上保持色彩统一和材质层次统一，优先精简配色与干净线条，会更贴合你的脸部基础。',
    },
    improvementPlan: {
      diagnosis: isMale
        ? '综合诊断：你当前属于中上颜值段位，最终评估分数在 7.6 分上下（10 分制），核心优势在于轮廓结构稳定和五官比例协调。短期内通过发型、肤质管理与妆感优化可明显提升镜头精致度；中长期通过稳定作息和持续护理，颜值表现有望继续上探，形成更强个人辨识度。'
        : '综合诊断：你当前属于中上偏高颜值段位，最终评估分数在 7.8 分上下（10 分制）。核心优势是五官协调、骨相支撑与气质表达自然。短期通过妆容结构优化可快速提升精致度；中长期通过护肤和形象管理持续迭代，可将“耐看”升级为“高辨识度高级感”。',
      focusedCare: '专注护理方案建议按肤质执行：若偏油，重点控油和毛孔管理；若偏干，重点补水和屏障修护；若混合肌，执行“T 区控油+两颊保湿”双策略。早晚都要温和清洁，避免过度去角质；每周做 1-2 次深层补水修护，必要时增加抗氧化精华，保持肤质稳定。',
      makeupOptimization: '妆容优化建议分四步执行：底妆先薄后叠，控制面部厚重感；眼妆强化眼尾和睫毛根部，提升精神度；唇妆采用轮廓修正+中心提亮，让口部更立体；修容重点在颧侧和下颌边界，提升脸型利落感。建议每次只强化一个重点，避免全脸同时过重。',
      hairAndOutfit: '发型与穿搭建议围绕“强化骨相优势”展开：发型选择能修饰面中比例和下颌线的层次结构，避免完全贴头皮或过厚刘海。穿搭优先简洁剪裁和中低饱和配色，用面料质感提升高级度。配饰控制在 1-2 个重点，避免信息过载影响面部焦点。',
      longTermMaintenance: '长期维护建议：减少熬夜，尽量保证稳定睡眠节律；坚持全年防晒，防止光老化影响肤质与轮廓观感；保持规律运动提升气色与面部紧致度；每月做一次形象复盘（发型、妆感、肤况、穿搭），持续微调。长期坚持后，颜值表现会更稳、更高级、更耐看。',
    },
  };
};

const normalizeSectionScore = (
  raw: unknown,
  fallback: BeautySectionScore,
): BeautySectionScore => {
  const obj = raw && typeof raw === 'object' ? raw as Record<string, unknown> : {};
  return {
    score: toScore10(obj.score ?? obj['评分'], fallback.score),
    feature: ensureLength(toText(obj.feature ?? obj['特征'], fallback.feature), 26, fallback.feature),
    highlight: ensureLength(toText(obj.highlight ?? obj['优势亮点'], fallback.highlight), 26, fallback.highlight),
    optimize: ensureLength(toText(obj.optimize ?? obj['优化方向'], fallback.optimize), 26, fallback.optimize),
    complexionFit: ensureLength(toText(obj.complexionFit ?? obj['肤色适配'], fallback.complexionFit), 24, fallback.complexionFit),
    style: ensureLength(toText(obj.style ?? obj['适配风格'], fallback.style), 18, fallback.style),
  };
};

const normalizeBeautyReport = (raw: unknown, gender: BeautyGender): BeautyReport => {
  const fallback = defaultReportFor(gender);
  const obj = raw && typeof raw === 'object' ? raw as Record<string, unknown> : {};
  const facial = obj.facialAnalysis && typeof obj.facialAnalysis === 'object'
    ? obj.facialAnalysis as Record<string, unknown>
    : {};
  const bone = obj.boneAnalysis && typeof obj.boneAnalysis === 'object'
    ? obj.boneAnalysis as Record<string, unknown>
    : {};
  const skin = obj.skinAnalysis && typeof obj.skinAnalysis === 'object'
    ? obj.skinAnalysis as Record<string, unknown>
    : {};
  const structured = obj.structuredFeatures && typeof obj.structuredFeatures === 'object'
    ? obj.structuredFeatures as Record<string, unknown>
    : {};
  const styleCls = obj.styleClassification && typeof obj.styleClassification === 'object'
    ? obj.styleClassification as Record<string, unknown>
    : {};
  const beautyType = obj.beautyType && typeof obj.beautyType === 'object'
    ? obj.beautyType as Record<string, unknown>
    : {};
  const makeup = obj.makeupAdvice && typeof obj.makeupAdvice === 'object'
    ? obj.makeupAdvice as Record<string, unknown>
    : {};
  const improvement = obj.improvementPlan && typeof obj.improvementPlan === 'object'
    ? obj.improvementPlan as Record<string, unknown>
    : {};

  const keywordsRaw = toList(obj.keywords ?? obj['关键词']);
  const keywords = (keywordsRaw.length >= 3 ? keywordsRaw : fallback.keywords).slice(0, 3);

  const proportion = structured.proportion && typeof structured.proportion === 'object'
    ? structured.proportion as Record<string, unknown>
    : {};
  const structuredFeatures = {
    faceShape: toText(structured.faceShape ?? structured['脸型结构'], fallback.structuredFeatures.faceShape),
    eyeType: toText(structured.eyeType ?? structured['眼型结构'], fallback.structuredFeatures.eyeType),
    browType: toText(structured.browType ?? structured['眉型结构'], fallback.structuredFeatures.browType),
    noseType: toText(structured.noseType ?? structured['鼻型结构'], fallback.structuredFeatures.noseType),
    lipType: toText(structured.lipType ?? structured['唇型结构'], fallback.structuredFeatures.lipType),
    skinCondition: toText(structured.skinCondition ?? structured['皮肤状态'], fallback.structuredFeatures.skinCondition),
    symmetryObservation: toText(structured.symmetryObservation ?? structured['对称度观察'], fallback.structuredFeatures.symmetryObservation),
    proportion: {
      threeCourtTop: toBoundedNumber(proportion.threeCourtTop ?? proportion['上庭'], 0.6, 1.6, fallback.structuredFeatures.proportion.threeCourtTop),
      threeCourtMiddle: toBoundedNumber(proportion.threeCourtMiddle ?? proportion['中庭'], 0.6, 1.6, fallback.structuredFeatures.proportion.threeCourtMiddle),
      threeCourtBottom: toBoundedNumber(proportion.threeCourtBottom ?? proportion['下庭'], 0.6, 1.6, fallback.structuredFeatures.proportion.threeCourtBottom),
      fiveEyeRatio: toBoundedNumber(proportion.fiveEyeRatio ?? proportion['五眼比例'], 3.5, 6.5, fallback.structuredFeatures.proportion.fiveEyeRatio),
      interocularRatio: toBoundedNumber(proportion.interocularRatio ?? proportion['眼间距比'], 0.6, 1.8, fallback.structuredFeatures.proportion.interocularRatio),
      goldenRatioFit: toScore10(proportion.goldenRatioFit ?? proportion['黄金比例贴合度'], fallback.structuredFeatures.proportion.goldenRatioFit),
    },
  };
  const styleTagsRaw = toList(styleCls.styleTags ?? styleCls['风格标签']);
  const styleClassification = {
    primary: toText(styleCls.primary ?? styleCls['主风格'], fallback.styleClassification.primary),
    secondary: toText(styleCls.secondary ?? styleCls['次风格'], fallback.styleClassification.secondary),
    confidence: toConfidence100(styleCls.confidence ?? styleCls['置信度'], fallback.styleClassification.confidence),
    evidence: ensureLength(toText(styleCls.evidence ?? styleCls['判定依据'], fallback.styleClassification.evidence), 50, fallback.styleClassification.evidence),
    styleTags: (styleTagsRaw.length >= 3 ? styleTagsRaw : fallback.styleClassification.styleTags).slice(0, 3),
  };

  const facialAnalysis = {
    eyes: normalizeSectionScore(facial.eyes ?? facial['眼睛'], fallback.facialAnalysis.eyes),
    nose: normalizeSectionScore(facial.nose ?? facial['鼻子'], fallback.facialAnalysis.nose),
    lips: normalizeSectionScore(facial.lips ?? facial['嘴唇'], fallback.facialAnalysis.lips),
    faceShape: normalizeSectionScore(facial.faceShape ?? facial['脸型'], fallback.facialAnalysis.faceShape),
  };
  const boneAnalysis = {
    contourDepth: toScore10(bone.contourDepth ?? bone['轮廓立体度'], fallback.boneAnalysis.contourDepth),
    cheekboneShape: toScore10(bone.cheekboneShape ?? bone['颧骨形态'], fallback.boneAnalysis.cheekboneShape),
    jawline: toScore10(bone.jawline ?? bone['下颌线'], fallback.boneAnalysis.jawline),
    foreheadFullness: toScore10(bone.foreheadFullness ?? bone['额头饱满高度'], fallback.boneAnalysis.foreheadFullness),
    summary: ensureLength(toText(bone.summary ?? bone['总结'], fallback.boneAnalysis.summary), 90, fallback.boneAnalysis.summary),
  };
  const skinAnalysis = {
    texture: toScore10(skin.texture ?? skin['皮肤质感'], fallback.skinAnalysis.texture),
    toneEvenness: toScore10(skin.toneEvenness ?? skin['肤色均匀度'], fallback.skinAnalysis.toneEvenness),
    fineness: toScore10(skin.fineness ?? skin['肤质细腻度'], fallback.skinAnalysis.fineness),
    radiance: toScore10(skin.radiance ?? skin['光泽度'], fallback.skinAnalysis.radiance),
    skinType: toText(skin.skinType ?? skin['肤质类型'], fallback.skinAnalysis.skinType),
    summary: ensureLength(toText(skin.summary ?? skin['总结'], fallback.skinAnalysis.summary), 90, fallback.skinAnalysis.summary),
  };

  const modelOverall = toScore10(obj.overallScore ?? obj['颜值总分'], fallback.overallScore);
  const computedOverallRaw = average([
    average([
      facialAnalysis.eyes.score,
      facialAnalysis.nose.score,
      facialAnalysis.lips.score,
      facialAnalysis.faceShape.score,
    ]),
    average([
      boneAnalysis.contourDepth,
      boneAnalysis.cheekboneShape,
      boneAnalysis.jawline,
      boneAnalysis.foreheadFullness,
    ]),
    average([
      skinAnalysis.texture,
      skinAnalysis.toneEvenness,
      skinAnalysis.fineness,
      skinAnalysis.radiance,
    ]),
  ]);
  const computedOverall = strictCalibrateScore10(computedOverallRaw);
  const threeCourtDeviation = Math.abs(structuredFeatures.proportion.threeCourtTop - structuredFeatures.proportion.threeCourtMiddle)
    + Math.abs(structuredFeatures.proportion.threeCourtMiddle - structuredFeatures.proportion.threeCourtBottom);
  const fiveEyeDeviation = Math.abs(structuredFeatures.proportion.fiveEyeRatio - 5);
  const interocularDeviation = Math.abs(structuredFeatures.proportion.interocularRatio - 1);
  const proportionLogicScoreRaw = 9.1 - (threeCourtDeviation * 1.6 + fiveEyeDeviation * 0.9 + interocularDeviation * 1.2);
  const proportionLogicScore = toScore10(proportionLogicScoreRaw, 7.2);
  const goldenRatioLogicScore = toScore10(structuredFeatures.proportion.goldenRatioFit, fallback.structuredFeatures.proportion.goldenRatioFit);
  const logicLayerScore = clamp(
    Math.round((proportionLogicScore * 0.58 + goldenRatioLogicScore * 0.42) * 10) / 10,
    5.0,
    9.3,
  );
  const overallScore = clamp(
    Math.round((modelOverall * 0.2 + computedOverall * 0.55 + logicLayerScore * 0.25) * 10) / 10,
    4.8,
    9.3,
  );

  return {
    overallScore,
    overallEightWords: normalizeEightWords(obj.overallEightWords ?? obj['八字评价'], fallback.overallEightWords),
    keywords,
    overallSummary: ensureLength(toText(obj.overallSummary ?? obj['总体表现'], fallback.overallSummary), 90, fallback.overallSummary),
    structuredFeatures,
    styleClassification,
    facialAnalysis,
    boneAnalysis,
    skinAnalysis,
    beautyType: {
      typeName: toText(beautyType.typeName ?? beautyType['类型'], fallback.beautyType.typeName),
      detailText: ensureLength(toText(beautyType.detailText ?? beautyType['类型详解'], fallback.beautyType.detailText), 100, fallback.beautyType.detailText),
      coreAdvantages: ensureLength(toText(beautyType.coreAdvantages ?? beautyType['核心优势'], fallback.beautyType.coreAdvantages), 80, fallback.beautyType.coreAdvantages),
      styleAdvice: ensureLength(toText(beautyType.styleAdvice ?? beautyType['风格建议'], fallback.beautyType.styleAdvice), 80, fallback.beautyType.styleAdvice),
    },
    makeupAdvice: {
      base: ensureLength(toText(makeup.base ?? makeup['底妆'], fallback.makeupAdvice.base), 80, fallback.makeupAdvice.base),
      eyes: ensureLength(toText(makeup.eyes ?? makeup['眼妆'], fallback.makeupAdvice.eyes), 80, fallback.makeupAdvice.eyes),
      noseAndLips: ensureLength(toText(makeup.noseAndLips ?? makeup['鼻型与唇妆'], fallback.makeupAdvice.noseAndLips), 80, fallback.makeupAdvice.noseAndLips),
      contourAndStyle: ensureLength(toText(makeup.contourAndStyle ?? makeup['修容与整体风格'], fallback.makeupAdvice.contourAndStyle), 80, fallback.makeupAdvice.contourAndStyle),
    },
    improvementPlan: {
      diagnosis: ensureLength(toText(improvement.diagnosis ?? improvement['综合诊断'], fallback.improvementPlan.diagnosis), 100, fallback.improvementPlan.diagnosis),
      focusedCare: ensureLength(toText(improvement.focusedCare ?? improvement['专注护理方案'], fallback.improvementPlan.focusedCare), 100, fallback.improvementPlan.focusedCare),
      makeupOptimization: ensureLength(toText(improvement.makeupOptimization ?? improvement['妆容优化建议'], fallback.improvementPlan.makeupOptimization), 100, fallback.improvementPlan.makeupOptimization),
      hairAndOutfit: ensureLength(toText(improvement.hairAndOutfit ?? improvement['发型与穿搭建议'], fallback.improvementPlan.hairAndOutfit), 100, fallback.improvementPlan.hairAndOutfit),
      longTermMaintenance: ensureLength(toText(improvement.longTermMaintenance ?? improvement['长期维护'], fallback.improvementPlan.longTermMaintenance), 100, fallback.improvementPlan.longTermMaintenance),
    },
  };
};

const defaultStructuredExtractionFor = (gender: BeautyGender): BeautyStructuredExtraction => {
  const fallback = defaultReportFor(gender);
  return {
    structuredFeatures: fallback.structuredFeatures,
    styleClassification: fallback.styleClassification,
  };
};

const normalizeStructuredExtraction = (raw: unknown, gender: BeautyGender): BeautyStructuredExtraction => {
  const fallback = defaultStructuredExtractionFor(gender);
  const obj = raw && typeof raw === 'object' ? raw as Record<string, unknown> : {};
  const structured = obj.structuredFeatures && typeof obj.structuredFeatures === 'object'
    ? obj.structuredFeatures as Record<string, unknown>
    : (obj['结构化特征'] && typeof obj['结构化特征'] === 'object' ? obj['结构化特征'] as Record<string, unknown> : {});
  const styleCls = obj.styleClassification && typeof obj.styleClassification === 'object'
    ? obj.styleClassification as Record<string, unknown>
    : (obj['风格分类'] && typeof obj['风格分类'] === 'object' ? obj['风格分类'] as Record<string, unknown> : {});
  const proportion = structured.proportion && typeof structured.proportion === 'object'
    ? structured.proportion as Record<string, unknown>
    : {};

  const structuredFeatures: BeautyStructuredExtraction['structuredFeatures'] = {
    faceShape: toText(structured.faceShape ?? structured['脸型结构'], fallback.structuredFeatures.faceShape),
    eyeType: toText(structured.eyeType ?? structured['眼型结构'], fallback.structuredFeatures.eyeType),
    browType: toText(structured.browType ?? structured['眉型结构'], fallback.structuredFeatures.browType),
    noseType: toText(structured.noseType ?? structured['鼻型结构'], fallback.structuredFeatures.noseType),
    lipType: toText(structured.lipType ?? structured['唇型结构'], fallback.structuredFeatures.lipType),
    skinCondition: toText(structured.skinCondition ?? structured['皮肤状态'], fallback.structuredFeatures.skinCondition),
    symmetryObservation: toText(structured.symmetryObservation ?? structured['对称度观察'], fallback.structuredFeatures.symmetryObservation),
    proportion: {
      threeCourtTop: toBoundedNumber(proportion.threeCourtTop ?? proportion['上庭'], 0.6, 1.6, fallback.structuredFeatures.proportion.threeCourtTop),
      threeCourtMiddle: toBoundedNumber(proportion.threeCourtMiddle ?? proportion['中庭'], 0.6, 1.6, fallback.structuredFeatures.proportion.threeCourtMiddle),
      threeCourtBottom: toBoundedNumber(proportion.threeCourtBottom ?? proportion['下庭'], 0.6, 1.6, fallback.structuredFeatures.proportion.threeCourtBottom),
      fiveEyeRatio: toBoundedNumber(proportion.fiveEyeRatio ?? proportion['五眼比例'], 3.5, 6.5, fallback.structuredFeatures.proportion.fiveEyeRatio),
      interocularRatio: toBoundedNumber(proportion.interocularRatio ?? proportion['眼间距比'], 0.6, 1.8, fallback.structuredFeatures.proportion.interocularRatio),
      goldenRatioFit: toScore10(proportion.goldenRatioFit ?? proportion['黄金比例贴合度'], fallback.structuredFeatures.proportion.goldenRatioFit),
    },
  };
  const styleTagsRaw = toList(styleCls.styleTags ?? styleCls['风格标签']);
  const styleClassification: BeautyStructuredExtraction['styleClassification'] = {
    primary: toText(styleCls.primary ?? styleCls['主风格'], fallback.styleClassification.primary),
    secondary: toText(styleCls.secondary ?? styleCls['次风格'], fallback.styleClassification.secondary),
    confidence: toConfidence100(styleCls.confidence ?? styleCls['置信度'], fallback.styleClassification.confidence),
    evidence: ensureLength(toText(styleCls.evidence ?? styleCls['判定依据'], fallback.styleClassification.evidence), 30, fallback.styleClassification.evidence),
    styleTags: (styleTagsRaw.length >= 3 ? styleTagsRaw : fallback.styleClassification.styleTags).slice(0, 3),
  };

  return { structuredFeatures, styleClassification };
};

const mergeExtractionIntoReportPayload = (
  payload: unknown,
  extraction: BeautyStructuredExtraction,
): Record<string, unknown> => {
  const obj = payload && typeof payload === 'object' ? payload as Record<string, unknown> : {};
  const rawStructured = obj.structuredFeatures && typeof obj.structuredFeatures === 'object'
    ? obj.structuredFeatures as Record<string, unknown>
    : {};
  const rawProportion = rawStructured.proportion && typeof rawStructured.proportion === 'object'
    ? rawStructured.proportion as Record<string, unknown>
    : {};
  const rawStyle = obj.styleClassification && typeof obj.styleClassification === 'object'
    ? obj.styleClassification as Record<string, unknown>
    : {};

  return {
    ...obj,
    structuredFeatures: {
      ...extraction.structuredFeatures,
      ...rawStructured,
      proportion: {
        ...extraction.structuredFeatures.proportion,
        ...rawProportion,
      },
    },
    styleClassification: {
      ...extraction.styleClassification,
      ...rawStyle,
    },
  };
};

const isModelUnavailableError = (err: unknown): boolean => {
  const msg = err instanceof Error ? err.message : String(err || '');
  return /model|invalid|not found|unsupported|不存在|无效/i.test(msg);
};

const humanizeBeautyError = (err: unknown): string => {
  const text = err instanceof Error ? err.message : String(err || '分析失败');
  if (text.includes('请先登录')) return '请先登录后再进行颜值分析。';
  if (text.includes('会员')) return '当前账号暂无可用权限，请先完成会员激活。';
  if (text.includes('网络')) return '网络连接异常，请检查后重试。';
  if (text.includes('JSON') || text.includes('格式')) return 'AI 返回内容格式异常，请重试一次。';
  return text || '分析失败，请稍后再试。';
};

const loadBeautyHistory = (): BeautyHistoryRecord[] => {
  try {
    const raw = localStorage.getItem(BEAUTY_HISTORY_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((item) => item && typeof item === 'object' && item.report)
      .map((item) => {
        const recordGender: BeautyGender = item.gender === 'male' ? 'male' : 'female';
        return {
          id: String(item.id || ''),
          createdAt: Number(item.createdAt || 0),
          gender: recordGender,
          report: normalizeBeautyReport(item.report, recordGender),
        };
      })
      .filter((item) => item.id);
  } catch {
    return [];
  }
};

const analyzeBeauty = async (imageDataUrl: string, gender: BeautyGender): Promise<BeautyAnalyzeResult> => {
  const overallStartAt = performance.now();
  const genderText = gender === 'male' ? '男生' : '女生';
  const content = [
    {
      type: 'text' as const,
      text: `${BEAUTY_REPORT_PROMPT}\n用户性别：${genderText}\n请严格输出 JSON 对象。`,
    },
    {
      type: 'image_url' as const,
      image_url: { url: imageDataUrl },
    },
  ];
  const requestByMode = async (featureMode: string): Promise<{ report: BeautyReport; usedModel: string }> => {
    const response = await chatApi({
      messages: [{ role: 'user', content }],
      jsonMode: true,
      feature: 'beautyFace',
      featureMode,
    });
    const parsed = parseLooseJson(response.text || '');
    const full = response.full && typeof response.full === 'object' ? response.full as Record<string, unknown> : null;
    const meta = full?.meta && typeof full.meta === 'object' ? full.meta as Record<string, unknown> : null;
    return {
      report: normalizeBeautyReport(parsed, gender),
      usedModel: String(meta?.model || full?.model || '').trim() || featureMode,
    };
  };

  const singleStageStartAt = performance.now();
  const attemptPlans = ['image', 'beauty_report'].filter((featureMode, index, arr) => (
    featureMode && arr.indexOf(featureMode) === index
  ));

  let report: BeautyReport | null = null;
  let usedModel = attemptPlans[0] || 'image';
  let lastError: unknown = null;
  for (let i = 0; i < attemptPlans.length; i += 1) {
    const featureMode = attemptPlans[i];
    try {
      const result = await requestByMode(featureMode);
      report = result.report;
      usedModel = result.usedModel;
      lastError = null;
      break;
    } catch (err) {
      lastError = err;
      const msg = err instanceof Error ? err.message : String(err || '');
      const formatLikeError = /json|格式|parse|解析/i.test(msg);
      if ((!isModelUnavailableError(err) && !formatLikeError) || i === attemptPlans.length - 1) {
        throw err;
      }
    }
  }
  if (!report) {
    throw lastError || new Error('报告生成失败');
  }
  const singleStageMs = Math.max(300, Math.round(performance.now() - singleStageStartAt));
  const totalMs = Math.max(1000, Math.round(performance.now() - overallStartAt));

  return {
    report: report as BeautyReport,
    metrics: {
      totalMs,
      visionExtractMs: singleStageMs,
      reportGenerateMs: 0,
      visionModel: usedModel,
      reportModel: 'single_stage',
    },
  };
};

function LoadingOverlay({
  theme,
  progressPercent,
  stepIndex,
  elapsedMs,
  trace,
  estimatedRange,
}: {
  theme: GenderTheme;
  progressPercent: number;
  stepIndex: number;
  elapsedMs: number;
  trace: number[];
  estimatedRange: { min: number; max: number };
}) {
  const safeProgress = clamp(progressPercent, 2, 99);
  const stepText = ANALYSIS_STEPS[Math.max(0, Math.min(stepIndex, ANALYSIS_STEPS.length - 1))] || ANALYSIS_STEPS[0];
  const seconds = (elapsedMs / 1000).toFixed(1);

  const radius = 42;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference * (1 - safeProgress / 100);

  const safeTrace = trace.length > 1 ? trace : [safeProgress, safeProgress];
  const points = safeTrace
    .map((value, index) => {
      const x = safeTrace.length <= 1 ? 0 : (index / (safeTrace.length - 1)) * 100;
      const y = 100 - clamp(value, 1, 99);
      return `${x},${y}`;
    })
    .join(' ');

  return (
    <div className="fixed inset-0 z-[90] bg-slate-950/40 backdrop-blur-[2px] flex items-center justify-center p-4">
      <div className={`w-full max-w-[390px] rounded-2xl border ${theme.borderClass} bg-white shadow-[0_30px_80px_-45px_rgba(15,23,42,0.55)] overflow-hidden`}>
        <div className={`px-5 py-4 bg-gradient-to-r ${theme.panelGradientClass} text-white`}>
          <div className="text-lg font-bold">AI 颜值分析中</div>
          <div className="text-xs opacity-90 mt-1">预计 {estimatedRange.min}-{estimatedRange.max} 秒</div>
        </div>
        <div className="p-5 space-y-4">
          <div className="flex items-center gap-4">
            <svg width="96" height="96" viewBox="0 0 100 100" className="shrink-0">
              <circle cx="50" cy="50" r={radius} fill="none" stroke={theme.ringTrack} strokeWidth="8" />
              <circle
                cx="50"
                cy="50"
                r={radius}
                fill="none"
                stroke={theme.ringStroke}
                strokeWidth="8"
                strokeLinecap="round"
                strokeDasharray={circumference}
                strokeDashoffset={dashOffset}
                transform="rotate(-90 50 50)"
              />
              <text x="50" y="54" textAnchor="middle" fontSize="18" fontWeight="700" fill={theme.ringStroke}>
                {Math.round(safeProgress)}%
              </text>
            </svg>
            <div className="flex-1">
              <div className="text-sm text-slate-500">当前步骤</div>
              <div className="text-sm font-semibold text-slate-800 mt-0.5">{stepText}</div>
              <div className="text-xs text-slate-500 mt-2">已耗时 {seconds}s</div>
            </div>
          </div>

          <div className="rounded-xl border border-slate-100 p-3">
            <div className="text-xs text-slate-500 mb-2">分析进度曲线</div>
            <svg viewBox="0 0 100 100" className="w-full h-20">
              <polyline
                fill="none"
                stroke={theme.chartStroke}
                strokeWidth="2.2"
                strokeLinejoin="round"
                strokeLinecap="round"
                points={points}
              />
              <polygon points={`${points} 100,100 0,100`} fill={theme.chartFill} />
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function BeautyFaceTest({ onBack, onRequireLogin, onBeforeShowResult }: BeautyFaceTestProps) {
  const [step, setStep] = useState<'input' | 'result' | 'example'>('input');
  const [gender, setGender] = useState<BeautyGender | null>(null);
  const [imageDataUrl, setImageDataUrl] = useState('');
  const [report, setReport] = useState<BeautyReport | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [analysisDurationMs, setAnalysisDurationMs] = useState<number | null>(null);
  const [analysisMetrics, setAnalysisMetrics] = useState<BeautyAnalysisMetrics | null>(null);
  const [estimatedRange, setEstimatedRange] = useState<{ min: number; max: number }>({ ...DEFAULT_ESTIMATED_ANALYSIS_SECONDS });
  const [progressPercent, setProgressPercent] = useState(0);
  const [progressStep, setProgressStep] = useState(0);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [progressTrace, setProgressTrace] = useState<number[]>([]);
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [historyRecords, setHistoryRecords] = useState<BeautyHistoryRecord[]>([]);
  const [exampleReturnStep, setExampleReturnStep] = useState<'input' | 'result'>('input');

  const inputRef = useRef<HTMLInputElement | null>(null);
  const reportRef = useRef<HTMLDivElement | null>(null);

  const effectiveGender: BeautyGender = gender || 'female';
  const theme = useMemo(() => THEMES[effectiveGender], [effectiveGender]);
  const sampleTheme = THEMES.female;
  const sampleReport = useMemo<BeautyReport>(() => {
    const base = defaultReportFor('female');
    return {
      ...base,
      overallScore: 8.0,
      overallEightWords: '清秀灵动高级耐看',
      keywords: ['上镜辨识度', '骨相皮相均衡', '清甜高级感'],
      overallSummary:
        '整体观感清透、顺眼且耐看，五官线条干净，轮廓过渡自然，属于“第一眼舒服、第二眼有细节”的稳定上镜类型。面中与下颌比例平衡，眼唇表达友好，风格可塑性高。后续重点放在底妆通透、眼尾精修与发型层次强化，能够明显放大精致度与镜头记忆点。',
    };
  }, []);
  const canAnalyze = Boolean(gender && imageDataUrl && !isLoading);
  const openResultWithGuard = async (): Promise<boolean> => {
    const canShow = onBeforeShowResult ? await onBeforeShowResult('beauty') : true;
    if (!canShow) {
      setErrorMsg('你已使用过免费心理测试次数，当前报告需激活码解锁后查看。');
      return false;
    }
    return true;
  };
  const openExamplePage = () => {
    setExampleReturnStep(step === 'result' ? 'result' : 'input');
    setStep('example');
  };
  const closeExamplePage = () => {
    setStep(exampleReturnStep);
  };
  const handleTopBarBack = () => {
    if (step === 'example') {
      closeExamplePage();
      return;
    }
    onBack();
  };
  useEffect(() => {
    if (!isLoading) {
      setProgressPercent(0);
      setProgressStep(0);
      setElapsedMs(0);
      setProgressTrace([]);
      return;
    }
    const startedAt = performance.now();
    const timer = window.setInterval(() => {
      const elapsed = performance.now() - startedAt;
      const normalized = Math.min(1, elapsed / PROGRESS_EXPECTED_MS);
      const eased = 1 - Math.pow(1 - normalized, 2.2);
      const pct = clamp(Math.round(eased * 96), 4, 96);
      const stepIndex = Math.min(ANALYSIS_STEPS.length - 1, Math.floor((pct / 100) * ANALYSIS_STEPS.length));
      setElapsedMs(elapsed);
      setProgressPercent(pct);
      setProgressStep(stepIndex);
      setProgressTrace((prev) => [...prev.slice(-35), pct]);
    }, 220);
    return () => window.clearInterval(timer);
  }, [isLoading]);

  useEffect(() => {
    let cancelled = false;
    QRCode.toDataURL(SITE_URL, {
      width: 220,
      margin: 1,
      color: { dark: '#111827', light: '#ffffff' },
    })
      .then((url) => {
        if (!cancelled) setQrDataUrl(url);
      })
      .catch(() => {
        if (!cancelled) setQrDataUrl('');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setHistoryRecords(loadBeautyHistory());
  }, []);

  useEffect(() => {
    setEstimatedRange(toEstimatedRange(loadBeautyTimingHistory()));
  }, []);

  const saveHistoryRecord = (nextReport: BeautyReport, nextGender: BeautyGender) => {
    const record: BeautyHistoryRecord = {
      id: `beauty_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      createdAt: Date.now(),
      gender: nextGender,
      report: nextReport,
    };
    setHistoryRecords((prev) => {
      const merged = [record, ...prev].slice(0, MAX_BEAUTY_HISTORY);
      localStorage.setItem(BEAUTY_HISTORY_KEY, JSON.stringify(merged));
      return merged;
    });
  };

  const openHistoryRecord = (recordItem: BeautyHistoryRecord) => {
    setGender(recordItem.gender);
    setImageDataUrl('');
    setReport(normalizeBeautyReport(recordItem.report, recordItem.gender));
    setAnalysisDurationMs(null);
    setAnalysisMetrics(null);
    setErrorMsg('');
    void (async () => {
      if (await openResultWithGuard()) {
        setStep('result');
      }
    })();
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const raw = await loadImageDataUrl(file);
      const compressed = await compressImage(raw);
      setImageDataUrl(compressed || raw);
      setErrorMsg('');
    } catch {
      setImageDataUrl('');
      setErrorMsg('图片读取失败，请重新上传后重试。');
    }
  };

  const handleAnalyze = async () => {
    if (!gender || !imageDataUrl) {
      setErrorMsg('请先上传照片并选择男生/女生。');
      return;
    }
    setIsLoading(true);
    setErrorMsg('');
    setAnalysisDurationMs(null);
    setAnalysisMetrics(null);
    try {
      const analyzed = await analyzeBeauty(imageDataUrl, gender);
      const nextReport = analyzed.report;
      const metrics = analyzed.metrics;
      setReport(nextReport);
      saveHistoryRecord(nextReport, gender);
      setAnalysisDurationMs(metrics.totalMs);
      setAnalysisMetrics(metrics);
      const history = appendBeautyTimingHistory(metrics.totalMs);
      setEstimatedRange(toEstimatedRange(history));
      if (await openResultWithGuard()) {
        setStep('result');
      }
    } catch (err) {
      const msg = humanizeBeautyError(err);
      if (msg.includes('请先登录')) onRequireLogin?.();
      setErrorMsg(`分析失败：${msg}`);
    } finally {
      setIsLoading(false);
    }
  };

  const resetAll = () => {
    setStep('input');
    setReport(null);
    setErrorMsg('');
    setAnalysisDurationMs(null);
    setAnalysisMetrics(null);
  };

  const downloadImage = () => {
    const html2canvasFn = (window as unknown as {
      html2canvas?: (el: HTMLElement, opt?: unknown) => Promise<HTMLCanvasElement>;
    }).html2canvas;
    if (!html2canvasFn || !reportRef.current) {
      setErrorMsg('截图引擎尚未加载完成，请稍后再试。');
      return;
    }
    html2canvasFn(reportRef.current, {
      scale: 2,
      useCORS: true,
      backgroundColor: effectiveGender === 'male' ? '#f2f8ff' : '#fff6fa',
    }).then((canvas) => {
      const link = document.createElement('a');
      link.download = `颜值报告_${effectiveGender === 'male' ? '男生' : '女生'}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    });
  };

  const durationText = analysisDurationMs ? `${(analysisDurationMs / 1000).toFixed(2)} 秒` : '--';
  const stageDurationText = analysisMetrics
    ? (
      analysisMetrics.reportGenerateMs > 0
        ? `结构提取 ${(analysisMetrics.visionExtractMs / 1000).toFixed(2)} 秒｜报告生成 ${(analysisMetrics.reportGenerateMs / 1000).toFixed(2)} 秒`
        : `单阶段推理 ${(analysisMetrics.visionExtractMs / 1000).toFixed(2)} 秒`
    )
    : '--';
  const genderLabel = gender === 'male' ? '男生' : gender === 'female' ? '女生' : '未选择';
  const sampleFacialItems: Array<{ key: keyof BeautyReport['facialAnalysis']; label: string }> = [
    { key: 'eyes', label: '眼睛' },
    { key: 'nose', label: '鼻子' },
    { key: 'lips', label: '嘴唇' },
    { key: 'faceShape', label: '脸型' },
  ];
  const sampleFacialAvg = Number(
    (
      (sampleReport.facialAnalysis.eyes.score
      + sampleReport.facialAnalysis.nose.score
      + sampleReport.facialAnalysis.lips.score
      + sampleReport.facialAnalysis.faceShape.score) / 4
    ).toFixed(1),
  );
  const sampleBoneAvg = Number(
    (
      (sampleReport.boneAnalysis.contourDepth
      + sampleReport.boneAnalysis.cheekboneShape
      + sampleReport.boneAnalysis.jawline
      + sampleReport.boneAnalysis.foreheadFullness) / 4
    ).toFixed(1),
  );
  const sampleSkinAvg = Number(
    (
      (sampleReport.skinAnalysis.texture
      + sampleReport.skinAnalysis.toneEvenness
      + sampleReport.skinAnalysis.fineness
      + sampleReport.skinAnalysis.radiance) / 4
    ).toFixed(1),
  );

  const renderScoreBar = (score: number, size: 'sm' | 'md' | 'lg' = 'md') => {
    const percentage = (score / 10) * 100;
    const heightClass = size === 'sm' ? 'h-1.5' : size === 'lg' ? 'h-3' : 'h-2';
    
    return (
      <div className={`${heightClass} rounded-full bg-slate-100 overflow-hidden relative`}>
        <div 
          className={`h-full rounded-full bg-gradient-to-r ${theme.panelGradientClass} transition-all duration-1000 ease-out`}
          style={{ 
            width: `${percentage}%`,
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
          }}
        />
      </div>
    );
  };

  const renderSampleScoreBar = (score: number, size: 'sm' | 'md' | 'lg' = 'md') => {
    const percentage = clamp((score / 10) * 100, 0, 100);
    const heightClass = size === 'sm' ? 'h-1.5' : size === 'lg' ? 'h-3' : 'h-2';
    return (
      <div className={`${heightClass} rounded-full bg-rose-100 overflow-hidden`}>
        <div
          className="h-full rounded-full bg-gradient-to-r from-fuchsia-500 to-rose-500"
          style={{ width: `${percentage}%` }}
        />
      </div>
    );
  };

  // 雷达图组件 - 展示五官/骨相/皮相分数
  const RadarChart: React.FC<{
    data: Array<{ label: string; value: number }>;
    size?: number;
  }> = ({ data, size = 200 }) => {
    const center = size / 2;
    const radius = size * 0.35;
    const angleStep = (2 * Math.PI) / data.length;
    
    // 计算每个点的坐标
    const getPoint = (index: number, value: number) => {
      const angle = index * angleStep - Math.PI / 2;
      const r = (value / 10) * radius;
      return {
        x: center + r * Math.cos(angle),
        y: center + r * Math.sin(angle),
      };
    };
    
    // 生成网格多边形
    const gridLevels = [2, 4, 6, 8, 10];
    
    // 生成数据区域路径
    const dataPoints = data.map((item, i) => getPoint(i, item.value));
    
    return (
      <div className="flex flex-col items-center">
        <svg width={size} height={size} className="overflow-visible">
          {/* 背景网格 */}
          {gridLevels.map((level) => {
            const points = data.map((_, i) => {
              const p = getPoint(i, level);
              return `${p.x},${p.y}`;
            }).join(' ');
            return (
              <polygon
                key={level}
                points={points}
                fill="none"
                stroke={theme.ringTrack}
                strokeWidth="1"
                strokeDasharray="3,3"
              />
            );
          })}
          
          {/* 轴线 */}
          {data.map((_, i) => {
            const end = getPoint(i, 10);
            return (
              <line
                key={i}
                x1={center}
                y1={center}
                x2={end.x}
                y2={end.y}
                stroke={theme.ringTrack}
                strokeWidth="1"
              />
            );
          })}
          
          {/* 数据区域 */}
          <polygon
            points={dataPoints.map(p => `${p.x},${p.y}`).join(' ')}
            fill={theme.chartFill}
            stroke={theme.chartStroke}
            strokeWidth="2"
            className="transition-all duration-1000"
          />
          
          {/* 数据点 */}
          {dataPoints.map((p, i) => (
            <circle
              key={i}
              cx={p.x}
              cy={p.y}
              r="4"
              fill={theme.chartStroke}
              className="transition-all duration-1000"
            />
          ))}
        </svg>
        
        {/* 标签 */}
        <div className="grid grid-cols-2 gap-x-6 gap-y-1 mt-2 text-xs">
          {data.map((item, i) => (
            <div key={i} className="flex items-center gap-1.5">
              <span 
                className="w-2 h-2 rounded-full" 
                style={{ backgroundColor: theme.chartStroke }}
              />
              <span className="text-slate-600">{item.label}</span>
              <span className={`font-bold ${theme.scoreTextClass}`}>{item.value.toFixed(1)}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className={`min-h-screen ${theme.pageBgClass} text-slate-800 flex flex-col`}>
      <XuanYuanTopBar
        title={step === 'example' ? '轩辕国学·颜值样例' : '轩辕国学·颜值'}
        onBack={handleTopBarBack}
        variant="lightBlend"
      />

      {step === 'input' && (
        <div className="flex-1 overflow-y-auto p-4">
          <div className="max-w-2xl mx-auto space-y-4">
            <div className={`border ${theme.borderClass} rounded-2xl bg-white/86 p-5 shadow-sm backdrop-blur-sm`}>
              <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                <Sparkles className={`w-4 h-4 ${theme.textAccentClass}`} />
                上传照片并选择性别
              </h2>
              <p className="text-sm text-slate-500 mt-2">
                生成超详细颜值报告：10分总分、8字评价、关键词、五官/骨相/皮相评分、专属风格与化妆提升建议。
              </p>
              <button
                onClick={openExamplePage}
                className={`mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${theme.borderClass} ${theme.softBgClass} ${theme.textAccentClass} hover:opacity-80`}
              >
                <Eye className="w-4 h-4" />
                查看报告样例
              </button>


              <div
                onClick={() => inputRef.current?.click()}
                className={`mt-4 h-60 rounded-xl border-2 border-dashed ${theme.borderClass} ${theme.softBgClass} cursor-pointer overflow-hidden flex items-center justify-center`}
              >
                {imageDataUrl ? (
                  <img src={imageDataUrl} alt="上传照片" className="max-w-full max-h-full w-auto h-auto object-contain" />
                ) : (
                  <div className={`text-center ${theme.textAccentClass}`}>
                    <ImageIcon className="w-8 h-8 mx-auto mb-2" />
                    <div className="text-sm">点击上传清晰人像照片</div>
                  </div>
                )}
              </div>
              <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={handleUpload} />

              <div className="mt-4">
                <div className="text-sm font-semibold text-slate-700 mb-2">选择性别</div>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setGender('male')}
                    className={`py-3 rounded-xl border text-sm font-semibold transition-colors ${
                      gender === 'male'
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    男生
                  </button>
                  <button
                    onClick={() => setGender('female')}
                    className={`py-3 rounded-xl border text-sm font-semibold transition-colors ${
                      gender === 'female'
                        ? 'bg-rose-600 text-white border-rose-600'
                        : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    女生
                  </button>
                </div>
              </div>

              <button
                onClick={handleAnalyze}
                disabled={!canAnalyze}
                className={`w-full mt-5 py-3.5 rounded-xl text-white font-bold flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed ${theme.primaryButtonClass}`}
              >
                {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {isLoading ? 'AI 计算中...' : '开始颜值分析'}
              </button>
              <div className="text-xs text-slate-400 mt-2">当前设置：{genderLabel}</div>
            </div>

            {errorMsg && (
              <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700 flex items-start gap-2">
                <XCircle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>{errorMsg}</span>
              </div>
            )}

            {historyRecords.length > 0 && (
              <div className={`bg-white border ${theme.borderClass} rounded-2xl p-4 shadow-sm`}>
                <h3 className="text-sm font-bold text-slate-800 mb-3">历史报告</h3>
                <div className="space-y-2 max-h-56 overflow-y-auto">
                  {historyRecords.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => openHistoryRecord(item)}
                      className={`w-full text-left rounded-xl border ${theme.borderClass} ${theme.softBgClass} px-3 py-2 hover:opacity-90`}
                    >
                      <div className="text-xs text-slate-500">{formatHistoryTime(item.createdAt)}</div>
                      <div className="text-sm font-semibold text-slate-800 mt-0.5">
                        {item.report.overallEightWords} · {item.report.overallScore.toFixed(1)}/10 · {item.gender === 'male' ? '男生' : '女生'}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {step === 'result' && report && (
        <>
          <div className="flex-1 overflow-y-auto p-4 pb-28">
            <div ref={reportRef} className="max-w-2xl mx-auto space-y-4">
              <div className={`bg-gradient-to-br ${theme.panelGradientClass} text-white rounded-2xl p-6 shadow-sm`}>
                <div className="text-xs tracking-wider uppercase opacity-90">专业颜值分析报告</div>
                <div className="text-center mt-4">
                  <div className="text-sm opacity-90">颜值总分</div>
                  <div className="mt-1 inline-flex items-end justify-center gap-1.5">
                    <div className="text-6xl font-black leading-none">{report.overallScore.toFixed(1)}</div>
                    <div className="text-base font-semibold pb-1 opacity-90">/10</div>
                  </div>
                  <div className="text-lg font-semibold mt-3 tracking-wider">{report.overallEightWords}</div>
                </div>
                <div className="flex flex-wrap justify-center gap-2 mt-4">
                  {report.keywords.map((tag) => (
                    <span key={tag} className="text-xs px-2.5 py-1 rounded-full bg-white/20">{tag}</span>
                  ))}
                </div>
                <p className="text-sm leading-7 mt-4 text-white/95">{report.overallSummary}</p>
                <div className="mt-4 flex items-center justify-center gap-2 text-xs">
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-white/20 px-3 py-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-white/80" />
                    {effectiveGender === 'male' ? '男生蓝色主题' : '女生粉色主题'}
                  </span>
                </div>
              </div>

              {imageDataUrl && (
                <div className={`bg-white rounded-2xl p-4 border ${theme.borderClass} shadow-sm`}>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-bold text-slate-900">测试照片</h4>
                    <button
                      onClick={openExamplePage}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${theme.borderClass} ${theme.softBgClass} ${theme.textAccentClass} hover:opacity-80`}
                    >
                      <Eye className="w-3.5 h-3.5" />
                      查看报告样例
                    </button>
                  </div>
                  <div className={`rounded-xl ${theme.softBgClass} p-3 flex items-center justify-center`}>
                    <img 
                      src={imageDataUrl} 
                      alt="测试照片" 
                      className="max-w-full w-auto h-auto object-contain rounded-lg" 
                      style={{ maxHeight: 'min(60vh, 600px)' }}
                    />
                  </div>
                </div>
              )}

              <div className={`bg-white rounded-2xl p-5 border ${theme.borderClass} shadow-sm`}>
                <h3 className="font-bold text-slate-900 mb-3">结构化特征与逻辑层判断</h3>
                <div className="grid gap-3 md:grid-cols-2 text-sm">
                  <div className={`rounded-xl border ${theme.borderClass} ${theme.softBgClass} p-4`}>
                    <h4 className={`font-semibold ${theme.textAccentClass} mb-2`}>视觉层结构化特征</h4>
                    <div className="space-y-1.5 text-slate-700 leading-6">
                      <div>脸型：{report.structuredFeatures.faceShape}</div>
                      <div>眼型：{report.structuredFeatures.eyeType}</div>
                      <div>眉型：{report.structuredFeatures.browType}</div>
                      <div>鼻型：{report.structuredFeatures.noseType}</div>
                      <div>唇型：{report.structuredFeatures.lipType}</div>
                      <div>肤况：{report.structuredFeatures.skinCondition}</div>
                    </div>
                  </div>
                  <div className={`rounded-xl border ${theme.borderClass} ${theme.softBgClass} p-4`}>
                    <h4 className={`font-semibold ${theme.textAccentClass} mb-2`}>逻辑层比例评估</h4>
                    <div className="space-y-2 text-slate-700">
                      <div>三庭比例：{report.structuredFeatures.proportion.threeCourtTop.toFixed(2)} : {report.structuredFeatures.proportion.threeCourtMiddle.toFixed(2)} : {report.structuredFeatures.proportion.threeCourtBottom.toFixed(2)}</div>
                      <div>五眼比例：{report.structuredFeatures.proportion.fiveEyeRatio.toFixed(2)}（理想≈5.00）</div>
                      <div>眼间距比：{report.structuredFeatures.proportion.interocularRatio.toFixed(2)}（理想≈1.00）</div>
                      <div className="pt-1">
                        <div className="flex items-center justify-between text-xs mb-1">
                          <span className="font-semibold">黄金比例贴合度</span>
                          <span className={theme.scoreTextClass}>{report.structuredFeatures.proportion.goldenRatioFit.toFixed(1)}/10</span>
                        </div>
                        {renderScoreBar(report.structuredFeatures.proportion.goldenRatioFit)}
                      </div>
                      <div className="text-xs text-slate-500">对称观察：{report.structuredFeatures.symmetryObservation}</div>
                    </div>
                  </div>
                </div>
                <div className={`mt-3 rounded-xl border ${theme.borderClass} p-3 ${theme.softBgClass}`}>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`text-xs px-2 py-1 rounded-full ${theme.chipClass} font-semibold`}>主风格：{report.styleClassification.primary}</span>
                    <span className={`text-xs px-2 py-1 rounded-full ${theme.chipClass} font-semibold`}>次风格：{report.styleClassification.secondary}</span>
                    <span className={`text-xs px-2 py-1 rounded-full ${theme.chipClass} font-semibold`}>置信度：{report.styleClassification.confidence}%</span>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {report.styleClassification.styleTags.map((tag) => (
                      <span key={tag} className="text-[11px] px-2 py-0.5 rounded-full bg-white border border-slate-200 text-slate-600">{tag}</span>
                    ))}
                  </div>
                  <p className="text-sm text-slate-600 leading-7 mt-2">{report.styleClassification.evidence}</p>
                </div>
                </div>
              </div>

              <div className={`bg-white rounded-2xl p-5 border ${theme.borderClass} shadow-sm`}>
                <h3 className="font-bold text-slate-900 mb-3">颜值分项分析</h3>
                <div className="space-y-4">

                {/* 雷达图总览 */}
                <div className={`rounded-xl border ${theme.borderClass} ${theme.softBgClass} p-4 mb-4`}>
                  <h4 className={`font-semibold ${theme.textAccentClass} mb-3 text-center`}>综合评分雷达图</h4>
                  <div className="grid md:grid-cols-2 gap-4">
                    <RadarChart 
                      data={[
                        { label: '眼睛', value: report.facialAnalysis.eyes.score },
                        { label: '鼻子', value: report.facialAnalysis.nose.score },
                        { label: '嘴唇', value: report.facialAnalysis.lips.score },
                        { label: '脸型', value: report.facialAnalysis.faceShape.score },
                      ]}
                      size={180}
                    />
                    <div className="space-y-3 flex flex-col justify-center">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-600">五官平均分</span>
                        <span className={`font-bold ${theme.scoreTextClass}`}>
                          {((report.facialAnalysis.eyes.score + report.facialAnalysis.nose.score + report.facialAnalysis.lips.score + report.facialAnalysis.faceShape.score) / 4).toFixed(1)}
                        </span>
                      </div>
                      {renderScoreBar((report.facialAnalysis.eyes.score + report.facialAnalysis.nose.score + report.facialAnalysis.lips.score + report.facialAnalysis.faceShape.score) / 4, 'sm')}
                      
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-600">骨相平均分</span>
                        <span className={`font-bold ${theme.scoreTextClass}`}>
                          {((report.boneAnalysis.contourDepth + report.boneAnalysis.cheekboneShape + report.boneAnalysis.jawline + report.boneAnalysis.foreheadFullness) / 4).toFixed(1)}
                        </span>
                      </div>
                      {renderScoreBar((report.boneAnalysis.contourDepth + report.boneAnalysis.cheekboneShape + report.boneAnalysis.jawline + report.boneAnalysis.foreheadFullness) / 4, 'sm')}
                      
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-600">皮相平均分</span>
                        <span className={`font-bold ${theme.scoreTextClass}`}>
                          {((report.skinAnalysis.texture + report.skinAnalysis.toneEvenness + report.skinAnalysis.fineness + report.skinAnalysis.radiance) / 4).toFixed(1)}
                        </span>
                      </div>
                      {renderScoreBar((report.skinAnalysis.texture + report.skinAnalysis.toneEvenness + report.skinAnalysis.fineness + report.skinAnalysis.radiance) / 4, 'sm')}
                    </div>
                  </div>
                </div>
                  <div>
                    <h4 className={`font-semibold ${theme.textAccentClass} mb-2`}>1. 五官分析</h4>
                    <div className="grid gap-3 md:grid-cols-2">
                      {[
                        ['眼睛', report.facialAnalysis.eyes],
                        ['鼻子', report.facialAnalysis.nose],
                        ['嘴唇', report.facialAnalysis.lips],
                        ['脸型', report.facialAnalysis.faceShape],
                      ].map(([label, data]) => (
                        <div key={label} className={`rounded-xl border ${theme.borderClass} ${theme.softBgClass} p-4`}>
                          <div className="flex items-end justify-between mb-2">
                            <div className="font-semibold text-slate-900">{label}</div>
                            <div className={`text-xl font-black ${theme.scoreTextClass}`}>{(data as BeautySectionScore).score.toFixed(1)}<span className="text-sm text-slate-500">/10</span></div>
                          </div>
                          {renderScoreBar((data as BeautySectionScore).score)}
                          <div className="mt-3 space-y-2 text-sm leading-7 text-slate-600">
                            <p><span className="font-semibold text-slate-800">特征：</span>{(data as BeautySectionScore).feature}</p>
                            <p><span className="font-semibold text-slate-800">优势亮点：</span>{(data as BeautySectionScore).highlight}</p>
                            <p><span className="font-semibold text-slate-800">优化方向：</span>{(data as BeautySectionScore).optimize}</p>
                            <p><span className="font-semibold text-slate-800">肤色适配：</span>{(data as BeautySectionScore).complexionFit}</p>
                            <p><span className="font-semibold text-slate-800">适配风格：</span>{(data as BeautySectionScore).style}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h4 className={`font-semibold ${theme.textAccentClass} mb-2`}>2. 骨相分析</h4>
                    <div className={`rounded-xl border ${theme.borderClass} ${theme.softBgClass} p-4`}>
                      <div className="space-y-3 text-sm">
                        {[
                          ['轮廓立体度', report.boneAnalysis.contourDepth],
                          ['颧骨形态', report.boneAnalysis.cheekboneShape],
                          ['下颌线', report.boneAnalysis.jawline],
                          ['额头饱满高度', report.boneAnalysis.foreheadFullness],
                        ].map(([label, score]) => (
                          <div key={label as string} className="flex items-center gap-3">
                            <div className="w-24 text-slate-700 text-xs font-semibold">{label as string}</div>
                            <div className="flex-1">{renderScoreBar(score as number)}</div>
                            <div className={`w-12 text-right font-black ${theme.scoreTextClass}`}>{(score as number).toFixed(1)}</div>
                          </div>
                        ))}
                      </div>
                      <p className="text-sm text-slate-600 leading-7 mt-4">{report.boneAnalysis.summary}</p>
                    </div>
                  </div>

                  <div>
                    <h4 className={`font-semibold ${theme.textAccentClass} mb-2`}>3. 皮相分析</h4>
                    <div className={`rounded-xl border ${theme.borderClass} ${theme.softBgClass} p-4`}>
                      <div className="space-y-3 text-sm">
                        {[
                          ['皮肤质感', report.skinAnalysis.texture],
                          ['肤色均匀度', report.skinAnalysis.toneEvenness],
                          ['肤质细腻度', report.skinAnalysis.fineness],
                          ['光泽度', report.skinAnalysis.radiance],
                        ].map(([label, score]) => (
                          <div key={label as string} className="flex items-center gap-3">
                            <div className="w-24 text-slate-700 text-xs font-semibold">{label as string}</div>
                            <div className="flex-1">{renderScoreBar(score as number)}</div>
                            <div className={`w-12 text-right font-black ${theme.scoreTextClass}`}>{(score as number).toFixed(1)}</div>
                          </div>
                        ))}
                      </div>
                      <div className="mt-3 text-xs text-slate-500">肤质类型：{report.skinAnalysis.skinType}</div>
                      <p className="text-sm text-slate-600 leading-7 mt-2">{report.skinAnalysis.summary}</p>
                    </div>
                  </div>
              </div>

              <div className={`bg-white rounded-2xl p-5 border ${theme.borderClass} shadow-sm`}>
                <h3 className="font-bold text-slate-900 mb-3">颜值类型详解</h3>
                <div className={`inline-flex text-xs px-2.5 py-1 rounded-full ${theme.chipClass} font-semibold mb-3`}>{report.beautyType.typeName}</div>
                <p className="text-sm text-slate-600 leading-7">{report.beautyType.detailText}</p>
                <div className="mt-4">
                  <h4 className={`font-semibold ${theme.textAccentClass} mb-1.5`}>照片核心专属优势</h4>
                  <p className="text-sm text-slate-600 leading-7">{report.beautyType.coreAdvantages}</p>
                </div>
                <div className="mt-4">
                  <h4 className={`font-semibold ${theme.textAccentClass} mb-1.5`}>照片风格专属建议</h4>
                  <p className="text-sm text-slate-600 leading-7">{report.beautyType.styleAdvice}</p>
                </div>
              </div>

              <div className={`bg-white rounded-2xl p-5 border ${theme.borderClass} shadow-sm`}>
                <h3 className="font-bold text-slate-900 mb-3">专业化妆建议</h3>
                <div className="space-y-4 text-sm text-slate-600 leading-7">
                  <div>
                    <div className={`font-semibold ${theme.textAccentClass}`}>1. 底妆</div>
                    <div>{report.makeupAdvice.base}</div>
                  </div>
                  <div>
                    <div className={`font-semibold ${theme.textAccentClass}`}>2. 眼妆</div>
                    <div>{report.makeupAdvice.eyes}</div>
                  </div>
                  <div>
                    <div className={`font-semibold ${theme.textAccentClass}`}>3. 鼻型与唇妆</div>
                    <div>{report.makeupAdvice.noseAndLips}</div>
                  </div>
                  <div>
                    <div className={`font-semibold ${theme.textAccentClass}`}>4. 修容与整体风格</div>
                    <div>{report.makeupAdvice.contourAndStyle}</div>
                  </div>
                </div>
              </div>

              <div className={`bg-white rounded-2xl p-5 border ${theme.borderClass} shadow-sm`}>
                <h3 className="font-bold text-slate-900 mb-3">专业颜值提升建议</h3>
                <div className="space-y-4 text-sm text-slate-600 leading-7">
                  <div>
                    <div className={`font-semibold ${theme.textAccentClass}`}>1. 综合诊断</div>
                    <div>{report.improvementPlan.diagnosis}</div>
                  </div>
                  <div>
                    <div className={`font-semibold ${theme.textAccentClass}`}>2. 专注护理方案</div>
                    <div>{report.improvementPlan.focusedCare}</div>
                  </div>
                  <div>
                    <div className={`font-semibold ${theme.textAccentClass}`}>3. 妆容优化建议</div>
                    <div>{report.improvementPlan.makeupOptimization}</div>
                  </div>
                  <div>
                    <div className={`font-semibold ${theme.textAccentClass}`}>4. 发型与穿搭建议</div>
                    <div>{report.improvementPlan.hairAndOutfit}</div>
                  </div>
                  <div>
                    <div className={`font-semibold ${theme.textAccentClass}`}>5. 长期维护</div>
                    <div>{report.improvementPlan.longTermMaintenance}</div>
                  </div>
                </div>
              </div>

              <XuanYuanReportFooter qrDataUrl={qrDataUrl} />

              <div className={`text-xs ${theme.textAccentClass} ${theme.softBgClass} border ${theme.borderClass} rounded-xl px-3 py-2 flex items-center gap-1.5`}>
                <CheckCircle2 className="w-3.5 h-3.5" />
                报告已生成，可导出图片保存或分享。
              </div>
            </div>
          </div>

          <div className="fixed bottom-6 left-4 right-4 max-w-2xl mx-auto flex gap-3">
            <button
              onClick={resetAll}
              className="flex-1 py-3 rounded-xl bg-white border border-slate-200 text-slate-700 font-semibold flex items-center justify-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              重新测试
            </button>
            <button
              onClick={downloadImage}
              className={`flex-1 py-3 rounded-xl text-white font-semibold flex items-center justify-center gap-2 ${theme.primaryButtonClass}`}
            >
              <Download className="w-4 h-4" />
              保存报告
            </button>
          </div>
        </>
      )}

      {step === 'example' && (
        <div className="flex-1 overflow-y-auto bg-[#fff7fb] p-4 pb-8">
          <div className="mx-auto w-full max-w-[390px] space-y-3">
            <div className={`relative overflow-hidden rounded-2xl bg-gradient-to-br ${sampleTheme.panelGradientClass} p-5 text-white`}>
              <div className="absolute -right-10 -top-12 h-36 w-36 rounded-full bg-white/25 blur-2xl" />
              <div className="absolute -left-12 bottom-[-58px] h-40 w-40 rounded-full bg-white/20 blur-2xl" />
              <div className="relative z-[1]">
                <div className="text-[11px] tracking-[0.18em] opacity-95">AI 颜值综合报告（样例）</div>
                <div className="mt-2 flex items-end justify-between">
                  <div>
                    <div className="inline-flex items-end gap-1.5">
                      <div className="text-5xl font-black leading-none">{sampleReport.overallScore.toFixed(1)}</div>
                      <div className="text-base font-semibold pb-1 opacity-90">/10</div>
                    </div>
                    <div className="text-xs opacity-90 mt-1">{sampleReport.overallEightWords}</div>
                  </div>
                  <div className="flex flex-wrap justify-end gap-1 text-[11px]">
                    {sampleReport.keywords.map((tag) => (
                      <span key={tag} className="rounded-full bg-white/25 px-2 py-0.5">{tag}</span>
                    ))}
                  </div>
                </div>
                <p className="mt-3 text-xs leading-5 text-white/90">{sampleReport.overallSummary}</p>
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-rose-100 p-3">
              <div className="text-xs text-slate-500 mb-2">示例照片</div>
              <img src={EXAMPLE_REPORT_IMAGE_URL} alt="颜值报告样例照片" className="w-full rounded-xl object-cover" />
            </div>

            <div className="bg-white rounded-2xl border border-rose-100 p-4">
              <h3 className="font-bold text-slate-900 mb-3">综合打分总览</h3>
              <div className="rounded-xl border border-rose-100 bg-rose-50/50 p-3">
                <div className="grid grid-cols-2 gap-3 items-center">
                  <RadarChart
                    data={[
                      { label: '眼', value: sampleReport.facialAnalysis.eyes.score },
                      { label: '鼻', value: sampleReport.facialAnalysis.nose.score },
                      { label: '唇', value: sampleReport.facialAnalysis.lips.score },
                      { label: '脸', value: sampleReport.facialAnalysis.faceShape.score },
                      { label: '骨', value: sampleBoneAvg },
                      { label: '皮', value: sampleSkinAvg },
                    ]}
                    size={168}
                  />
                  <div className="space-y-2">
                    {[
                      ['综合总分', sampleReport.overallScore],
                      ['五官均分', sampleFacialAvg],
                      ['骨骼均分', sampleBoneAvg],
                      ['皮肤均分', sampleSkinAvg],
                    ].map(([label, score]) => (
                      <div key={String(label)}>
                        <div className="flex items-center justify-between text-xs mb-1">
                          <span className="text-slate-600">{label}</span>
                          <span className={`font-bold ${sampleTheme.scoreTextClass}`}>{Number(score).toFixed(1)}</span>
                        </div>
                        {renderSampleScoreBar(Number(score), 'sm')}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-rose-100 p-4">
              <h3 className="font-bold text-slate-900 mb-3">颜值分项分析</h3>
              <h4 className="font-semibold text-rose-700 mb-2">1. 五官分析</h4>
              <div className="space-y-2.5">
                {sampleFacialItems.map((item) => {
                  const data = sampleReport.facialAnalysis[item.key];
                  return (
                  <div key={item.label} className="rounded-xl border border-rose-100 bg-rose-50/50 p-3">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-slate-900">{item.label}</span>
                      <span className={`font-black ${sampleTheme.scoreTextClass}`}>{data.score.toFixed(1)}</span>
                    </div>
                    <div className="mt-1.5">{renderSampleScoreBar(data.score)}</div>
                    <div className="mt-2 space-y-1.5 text-xs leading-6 text-slate-600">
                      <p><span className="font-semibold text-slate-800">1. {item.label}特征：</span>{data.feature}</p>
                      <p><span className="font-semibold text-slate-800">2. 优势亮点：</span>{data.highlight}</p>
                      <p><span className="font-semibold text-slate-800">3. 优化方向：</span>{data.optimize}</p>
                      <p><span className="font-semibold text-slate-800">4. 肤色适配：</span>{data.complexionFit}</p>
                      <p><span className="font-semibold text-slate-800">5. 适配风格：</span>{data.style}</p>
                    </div>
                  </div>
                  );
                })}
              </div>

              <h4 className="font-semibold text-rose-700 mt-4 mb-2">2. 骨骼分析（5维）</h4>
              <div className="rounded-xl border border-rose-100 bg-rose-50/50 p-3 space-y-2">
                {([
                  ['轮廓立体度', sampleReport.boneAnalysis.contourDepth],
                  ['颧骨形态', sampleReport.boneAnalysis.cheekboneShape],
                  ['下颌线', sampleReport.boneAnalysis.jawline],
                  ['额头饱满度', sampleReport.boneAnalysis.foreheadFullness],
                ] as const).map(([label, score]) => (
                  <div key={label}>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="text-slate-600">{label}</span>
                      <span className={`font-bold ${sampleTheme.scoreTextClass}`}>{score.toFixed(1)}</span>
                    </div>
                    {renderSampleScoreBar(score, 'sm')}
                  </div>
                ))}
                <div className="mt-2 space-y-1.5 text-xs leading-6 text-slate-600">
                  <p><span className="font-semibold text-slate-800">1. 骨骼特征：</span>{sampleReport.boneAnalysis.summary}</p>
                  <p><span className="font-semibold text-slate-800">2. 优势亮点：</span>骨相轮廓清晰，面中支撑稳定，正侧脸都能保持立体感。</p>
                  <p><span className="font-semibold text-slate-800">3. 优化方向：</span>发际线、颧侧和下颌线做轻修容，放大轮廓层次。</p>
                  <p><span className="font-semibold text-slate-800">4. 肤色适配：</span>黄调肤色更适合杏棕修容，白调肤色更适合灰棕修容。</p>
                  <p><span className="font-semibold text-slate-800">5. 适配风格：</span>通勤高级风、轻港风、简约质感风。</p>
                </div>
              </div>

              <h4 className="font-semibold text-rose-700 mt-4 mb-2">3. 皮肤分析（5维）</h4>
              <div className="rounded-xl border border-rose-100 bg-rose-50/50 p-3 space-y-2">
                {([
                  ['皮肤质感', sampleReport.skinAnalysis.texture],
                  ['肤色均匀度', sampleReport.skinAnalysis.toneEvenness],
                  ['肤质细腻度', sampleReport.skinAnalysis.fineness],
                  ['光泽度', sampleReport.skinAnalysis.radiance],
                ] as const).map(([label, score]) => (
                  <div key={label}>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="text-slate-600">{label}</span>
                      <span className={`font-bold ${sampleTheme.scoreTextClass}`}>{score.toFixed(1)}</span>
                    </div>
                    {renderSampleScoreBar(score, 'sm')}
                  </div>
                ))}
                <div className="mt-2 space-y-1.5 text-xs leading-6 text-slate-600">
                  <p><span className="font-semibold text-slate-800">1. 肤质特征：</span>{sampleReport.skinAnalysis.summary}</p>
                  <p><span className="font-semibold text-slate-800">2. 优势亮点：</span>肤色基础较均匀，皮相通透度高，近景观感干净。</p>
                  <p><span className="font-semibold text-slate-800">3. 优化方向：</span>T区控油+两颊补水，减少浮粉和暗沉波动。</p>
                  <p><span className="font-semibold text-slate-800">4. 肤色适配：</span>黄调肤色建议蜜桃杏色系，白调肤色建议玫瑰冷粉系。</p>
                  <p><span className="font-semibold text-slate-800">5. 适配风格：</span>清透裸感妆、轻雾面妆、自然光泽底妆。</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-rose-100 p-4">
              <h3 className="font-bold text-slate-900 mb-2">专业化妆建议（定制）</h3>
              <div className="space-y-2 text-xs text-slate-600 leading-6">
                <p><span className="font-semibold text-rose-700">按肤质定制：</span>按照你的「{sampleReport.skinAnalysis.skinType}」肤质，建议底妆走分区定制：T区控油定妆、两颊保湿打底，提升持妆与通透。</p>
                <p><span className="font-semibold text-rose-700">按眼型定制：</span>针对你的「{sampleReport.structuredFeatures.eyeType}」眼型，建议眼线后移并轻微上扬，睫毛根部加密，放大眼神聚焦。</p>
                <p><span className="font-semibold text-rose-700">按鼻型定制：</span>针对你的「{sampleReport.structuredFeatures.noseType}」鼻型，鼻影建议窄区纵向连接，高光点放在山根与鼻尖，避免面中显宽。</p>
                <p><span className="font-semibold text-rose-700">按唇型定制：</span>针对你的「{sampleReport.structuredFeatures.lipType}」唇型，建议先描边再分区上色，上唇略收、下唇中心提亮增强立体。</p>
                <p><span className="font-semibold text-rose-700">按脸型定制：</span>针对你的「{sampleReport.structuredFeatures.faceShape}」，修容建议沿颧侧至下颌轻量上提，保留自然留白更显高级。</p>
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-rose-100 p-4">
              <h3 className="font-bold text-slate-900 mb-2">颜值类型详解</h3>
              <span className={`inline-flex text-xs px-2 py-1 rounded-full ${sampleTheme.chipClass} font-semibold`}>{sampleReport.beautyType.typeName}</span>
              <p className="text-xs text-slate-600 leading-6 mt-2">{sampleReport.beautyType.detailText}</p>
              <p className="text-xs text-slate-600 leading-6 mt-2"><span className="font-semibold text-slate-800">核心优势：</span>{sampleReport.beautyType.coreAdvantages}</p>
              <p className="text-xs text-slate-600 leading-6 mt-2"><span className="font-semibold text-slate-800">风格建议：</span>{sampleReport.beautyType.styleAdvice}</p>
            </div>

            <div className="bg-white rounded-2xl border border-rose-100 p-4">
              <h3 className="font-bold text-slate-900 mb-2">专业颜值提升建议</h3>
              <div className="space-y-2 text-xs text-slate-600 leading-6">
                <p><span className="font-semibold text-rose-700">1. 综合诊断：</span>{sampleReport.improvementPlan.diagnosis}</p>
                <p><span className="font-semibold text-rose-700">2. 专注护理方案：</span>{sampleReport.improvementPlan.focusedCare}</p>
                <p><span className="font-semibold text-rose-700">3. 妆容优化建议：</span>{sampleReport.improvementPlan.makeupOptimization}</p>
                <p><span className="font-semibold text-rose-700">4. 发型与穿搭建议：</span>{sampleReport.improvementPlan.hairAndOutfit}</p>
                <p><span className="font-semibold text-rose-700">5. 长期维护：</span>{sampleReport.improvementPlan.longTermMaintenance}</p>
              </div>
            </div>

            <p className="text-xs text-center text-slate-500 px-2 pb-2">
              示例仅用于展示版式与结构，实际报告会根据上传照片自动生成。
            </p>
          </div>
        </div>
      )}


      {isLoading && (
        <LoadingOverlay
          theme={theme}
          progressPercent={progressPercent}
          stepIndex={progressStep}
          elapsedMs={elapsedMs}
          trace={progressTrace}
          estimatedRange={estimatedRange}
        />
      )}
    </div>
  );
}
